import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type GHLFollowUpStats,
  formatCost,
  calculateTotalCost,
  formatTokenCount,
} from '@/lib/ghl-dashboard'
import { Clock, Send, XCircle, Ban, Cpu } from 'lucide-react'

interface Props {
  readonly stats: GHLFollowUpStats | null | undefined
  readonly isLoading: boolean
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string
  value: string
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  isLoading: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-24 mt-1" />
            ) : (
              <p className="text-2xl font-bold">{value}</p>
            )}
            {subtitle && !isLoading && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <Icon className="h-8 w-8 text-muted-foreground/50" />
        </div>
      </CardContent>
    </Card>
  )
}

export function FollowUpStatsCards({ stats, isLoading }: Props) {
  // No model argument: get_ghl_followup_stats sums tokens across rows without a
  // model breakdown, so any literal here would misprice every row produced by a
  // different model. Falls through to DEFAULT_PRICING instead of guessing.
  const aiCost = stats
    ? calculateTotalCost(stats.total_input_tokens, stats.total_output_tokens)
    : 0

  const cancelledPct = stats && stats.total_scheduled > 0
    ? Math.round((stats.cancelled / stats.total_scheduled) * 100)
    : 0

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <StatCard
        title="Pending"
        value={stats?.pending.toLocaleString() ?? '0'}
        subtitle={`${stats?.upcoming_24h ?? 0} due in next 24h`}
        icon={Clock}
        isLoading={isLoading}
      />
      <StatCard
        title="Sent"
        value={stats?.sent.toLocaleString() ?? '0'}
        subtitle={`of ${stats?.total_scheduled.toLocaleString() ?? 0} total`}
        icon={Send}
        isLoading={isLoading}
      />
      <StatCard
        title="Failed"
        value={stats?.failed.toLocaleString() ?? '0'}
        subtitle={stats?.failed ? 'check error details below' : 'all clear'}
        icon={XCircle}
        isLoading={isLoading}
      />
      <StatCard
        title="Cancelled"
        value={stats?.cancelled.toLocaleString() ?? '0'}
        subtitle={`${cancelledPct}% of total`}
        icon={Ban}
        isLoading={isLoading}
      />
      <StatCard
        title="AI Cost"
        value={formatCost(aiCost)}
        subtitle={`${formatTokenCount((stats?.total_input_tokens ?? 0) + (stats?.total_output_tokens ?? 0))} tokens`}
        icon={Cpu}
        isLoading={isLoading}
      />
    </div>
  )
}
